import { datadogRum } from '@datadog/browser-rum-slim';
import { datadogLogs, Logger } from '@datadog/browser-logs';
import { isZeroSample } from './isZeroSample';
import { ENVIRONMENT, IS_DEV, IS_PROD, VERSION } from '../../../../constants';

export { type Logger } from '@datadog/browser-logs';

const environmentId = ENVIRONMENT;
const serviceName = 'voc-unmoderated-lobby';

if (IS_PROD) {
  const applicationId = '54a4ea6a-c8ad-492e-b2e4-4de07f56bb5c';
  const clientToken = 'pub423a66c5cb2a16b4fc5c00b8d6be65e5';

  const isRecording = window.location.search.includes('hjDebugSession=1');

  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.eu',
    service: serviceName,
    version: VERSION,
    env: environmentId,
    sessionSampleRate: isZeroSample() ? 0 : 100,
    sessionReplaySampleRate: 0,
    allowedTracingUrls: [/https:\/\/.*\.hotjar\.com/, /https:\/\/.*\.hotjar\.io/],
    excludedActivityUrls: [/^(?!.*(.hotjar.com|s3.amazonaws.com))/],
    useSecureSessionCookie: true,

    /*
      trackUserInteractions
      Elements marked with data-dd-action-name will be tracked as user actions.
      https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions/
    */
    trackUserInteractions: true,

    /*
      trackViewsManually
      Allows teams to override the default view names for the RUM views.
      https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm#override-default-rum-view-names
    */
    trackViewsManually: false,
    trackResources: true,
    trackLongTasks: false,
    enablePrivacyForActionName: true,
  });

  if (isRecording) {
    datadogRum.startSessionReplayRecording();
  }

  datadogLogs.init({
    clientToken: 'pub980ac3345f76d59f07e84bd74b4733c4',
    site: 'datadoghq.eu',
    service: serviceName,
    version: VERSION,
    env: environmentId,
    forwardErrorsToLogs: false, // Errors are tracked with RUM
    sessionSampleRate: isZeroSample() ? 0 : 100,
    forwardReports: ['deprecation', 'csp_violation'],
    telemetrySampleRate: 0,
    useSecureSessionCookie: true,
  });
}

/**
 * @summary Sends the handled error to Datadog RUM
 * @param {(string|Error)} error The caught error
 * @param  {object} [metadata] Metadata for the error
 * @return {undefined}
 * @example addRumError(error, {withMetaData: true});
 */
export function addRumError(error: unknown, metadata?: object) {
  datadogRum?.addError(error, metadata);
}

/**
 * @summary Sends custom RUM action to Datadog, useful for associating error with user actions
 * @param  {string} actionName Name of the action
 * @param  {object} [metadata] Metadata for the action
 * @return {undefined}
 * @example addRumAction('interview-user-clicked-this-thing', {withMetaData: true});
 */
export function addRumAction(actionName: string, metadata?: object) {
  datadogRum?.addAction(actionName, metadata);
}

/**
 * @summary Extends the global context for RUM events
 * @param  {string} propertyName Name of the context property
 * @param  {string|object} value Value of the context property
 * @return {undefined}
 * @example setGlobalContextProperty('session-id', 123456');
 */
export function setRumGlobalContextProperty(propertyName: string, context: object) {
  datadogRum?.setGlobalContextProperty(propertyName, context);
}

function createLogger() {
  const context = datadogRum?.getInternalContext();
  const ddLogger = datadogLogs?.createLogger(serviceName, {
    context: { team: 'Researcher Experience', ...context },
  });

  if (IS_DEV) {
    ddLogger.setHandler('console');
  }
  return ddLogger;
}

let logger: Logger | undefined = undefined;

export const getLogger = () => {
  return logger ?? (logger = createLogger());
};
